import { useApiQuery, useTeam } from '@vrplatform/data';
import { formatCurrency, isUUID, utc } from '@vrplatform/utils';
import { Group, Stack, Text, Title, rem, useMantineTheme } from '@mantine/core';
import { Box, Card as MantineCard } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import { Legend } from '@vrplatform/components';
import { ChartCard } from '../../../dashboard/legacy/_components/ChartCard';
import {
  getListingBalancesChartData,
  getListingBalancesChartRange,
  type ListingBalancesChartPoint,
} from './ListingBalancesChart.utils';

export const STORYBOOK_LISTING_BALANCES_CHART_QUERY_KEY =
  'listing-balances-chart';

type Props = {
  listingId: string;
};

type RevenueParty = 'owners' | 'manager';

function useListingBalancesChartQuery({ listingId }: Props) {
  const [{ id: teamId, effectiveStatementStartAt: statementStartAt }] =
    useTeam();
  const { apiEndDate, currentMonth, months, startDate } =
    getListingBalancesChartRange({
      statementStartAt,
    });

  return useApiQuery(
    async (api, signal, args) => {
      if (!args.listingId) {
        return {
          chartData: [],
          currency: undefined,
          totals: {
            manager: 0,
            owners: 0,
          },
        };
      }

      const sharedQuery = {
        dimension: 'month' as const,
        endDate: args.endDate,
        listingId: args.listingId,
        startDate: args.startDate,
      };

      const [ownerResult, managerResult] = await Promise.all([
        api.GET('/metrics/listing-revenue', {
          params: {
            query: {
              ...sharedQuery,
              party: 'owners',
            },
          },
          signal,
        }),
        api.GET('/metrics/listing-revenue', {
          params: {
            query: {
              ...sharedQuery,
              party: 'manager',
            },
          },
          signal,
        }),
      ]);

      if (ownerResult.error) {
        throw ownerResult.error;
      }

      if (managerResult.error) {
        throw managerResult.error;
      }

      return getListingBalancesChartData({
        currentMonth: args.currentMonth,
        managerRows: managerResult.data?.data ?? [],
        months: args.months,
        ownerRows: ownerResult.data?.data ?? [],
      });
    },
    {
      keepPreviousData: true,
      queryGroup: 'listings',
      skip: !listingId || !isUUID(listingId),
      storybookQueryKey: STORYBOOK_LISTING_BALANCES_CHART_QUERY_KEY,
      uniqueQueryKey: 'listing-balances-chart',
      variables: {
        currentMonth,
        endDate: apiEndDate,
        listingId,
        months,
        startDate,
        teamId,
      },
    }
  );
}

export const ListingBalancesChart = (props: Props) => {
  const [{ currency: teamCurrency }] = useTeam();

  const {
    isLoading: loading,
    error,
    data,
  } = useListingBalancesChartQuery(props);

  const theme = useMantineTheme();
  const chartData = data?.chartData ?? [];
  const currency = data?.currency ?? teamCurrency;

  const formatDate = (dateStr: string) => utc(dateStr).format('MMM YYYY');

  const getXAxisTicks = () => {
    if (chartData.length === 0) return [];
    if (chartData.length === 1) return [chartData[0].date];
    if (chartData.length === 2) return [chartData[0].date, chartData[1].date];

    const startIndex = 0;
    const middleIndex = Math.floor((chartData.length - 1) / 2);
    const endIndex = chartData.length - 1;

    return [
      chartData[startIndex].date,
      chartData[middleIndex].date,
      chartData[endIndex].date,
    ];
  };

  const ownerColor = theme.colors[theme.primaryColor][6];
  const managerColor = theme.colors.violet[5];
  const futureOpacity = 0.4;

  const ownerUnlockedPatternId = 'listingOwnerRevenueUnlockedPattern';
  const managerLockedPatternId = 'listingManagerRevenueLockedPattern';

  const horizontalPadding = 60;

  return (
    <ChartCard
      error={error}
      loading={loading}
      mih={270}
      w={'100%'}
      maw={700}
      p="lg"
      pb="sm"
      radius="lg"
    >
      <Group mb="lg">
        <Total
          value={formatCurrency(data?.totals.owners ?? 0, currency)}
          type="owners"
        />
        <Total
          value={formatCurrency(data?.totals.manager ?? 0, currency)}
          type="manager"
        />
      </Group>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 0, right: 0, left: 0, bottom: 5 }}
        >
          <defs>
            <pattern
              id={ownerUnlockedPatternId}
              patternUnits="userSpaceOnUse"
              width="5"
              height="5"
              patternTransform="rotate(45)"
            >
              <rect width="5" height="5" fill={ownerColor} />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="5"
                stroke="white"
                strokeWidth="2"
              />
            </pattern>
            <pattern
              id={managerLockedPatternId}
              patternUnits="userSpaceOnUse"
              width="5"
              height="5"
              patternTransform="rotate(45)"
            >
              <rect width="5" height="5" fill={managerColor} />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="5"
                stroke="white"
                strokeWidth="2"
              />
            </pattern>
          </defs>
          <CartesianGrid opacity={0.5} vertical={false} />
          <XAxis
            dataKey="date"
            ticks={getXAxisTicks()}
            tickFormatter={formatDate}
            scale="point"
            xAxisId={0}
            interval={0}
            tickLine={false}
            padding={{ left: horizontalPadding, right: horizontalPadding }}
            axisLine={false}
            tick={(props) => {
              const { x, y, payload, index } = props;
              if (typeof x !== 'number' || typeof y !== 'number') {
                return null;
              }

              const ticks = getXAxisTicks();
              let xPosition = x;
              const width = typeof props.width === 'number' ? props.width : x;

              if (index === 0) {
                xPosition = xPosition - horizontalPadding;
              } else if (index === ticks.length - 1) {
                xPosition = width - horizontalPadding;
              } else {
                xPosition = x - horizontalPadding / 2;
              }

              return (
                <g transform={`translate(${xPosition},${y})`}>
                  <text x={0} y={0} dy={16} fill="#737373" fontSize={12}>
                    {formatDate(payload.value)}
                  </text>
                </g>
              );
            }}
          />
          <Tooltip
            content={({ payload }) => {
              const point = payload?.[0]?.payload as
                | ListingBalancesChartPoint
                | undefined;

              if (!point) {
                return null;
              }

              return (
                <MantineCard p="xs" shadow="md" withBorder miw={200}>
                  <Text c="#525252" mb="sm">
                    {utc(point.date).format('MMMM YYYY')}
                  </Text>
                  <Stack gap={'xs'}>
                    <Group justify="space-between">
                      <Legend
                        legendColor={ownerColor}
                        label={'Owner Revenue'}
                        type="bar"
                      />
                      <Text ta="right">
                        {formatCurrency(point.ownerRevenue, currency)}
                      </Text>
                    </Group>
                    <Group
                      justify="space-between"
                      gap="sm"
                      pl="md"
                      wrap="nowrap"
                    >
                      <Text c="#737373" size="sm">
                        Locked Owner Revenue
                      </Text>
                      <Text size="sm" ta="right">
                        {formatCurrency(point.ownerLockedRevenue, currency)}
                      </Text>
                    </Group>
                    <Group
                      justify="space-between"
                      gap="sm"
                      pl="md"
                      wrap="nowrap"
                    >
                      <Text c="#737373" size="sm">
                        Unlocked Owner Revenue
                      </Text>
                      <Text size="sm" ta="right">
                        {formatCurrency(point.ownerUnlockedRevenue, currency)}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Legend
                        legendColor={managerColor}
                        label={'PM Revenue'}
                        type="bar"
                      />
                      <Text ta="right">
                        {formatCurrency(point.managerRevenue, currency)}
                      </Text>
                    </Group>
                  </Stack>
                </MantineCard>
              );
            }}
          />
          <Bar
            dataKey="ownerLockedRevenue"
            name="Locked Owner Revenue"
            stackId="owners"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`owner-locked-${index}`}
                fill={ownerColor}
                fillOpacity={entry.isFuture ? futureOpacity : 1}
                radius={entry.ownerUnlockedRevenue > 0 ? 0 : 3}
              />
            ))}
          </Bar>
          <Bar
            dataKey="ownerUnlockedRevenue"
            name="Unlocked Owner Revenue"
            stackId="owners"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`owner-unlocked-${index}`}
                fill={`url(#${ownerUnlockedPatternId})`}
                fillOpacity={entry.isFuture ? futureOpacity : 1}
                radius={3}
              />
            ))}
          </Bar>
          <Bar
            dataKey="managerLockedRevenue"
            name="Locked PM Revenue"
            stackId="manager"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`manager-locked-${index}`}
                fill={`url(#${managerLockedPatternId})`}
                fillOpacity={entry.isFuture ? futureOpacity : 1}
                radius={entry.managerUnlockedRevenue > 0 ? 0 : 3}
              />
            ))}
          </Bar>
          <Bar
            dataKey="managerUnlockedRevenue"
            name="Unlocked PM Revenue"
            stackId="manager"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`manager-unlocked-${index}`}
                fill={managerColor}
                fillOpacity={entry.isFuture ? futureOpacity : 1}
                radius={3}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const Total = ({ value, type }: { value: string; type: RevenueParty }) => {
  const theme = useMantineTheme();

  return (
    <Box>
      <Legend
        label={type === 'manager' ? 'PM Revenue' : 'Owner Revenue'}
        type="bar"
        legendColor={
          type === 'manager'
            ? theme.colors.violet[5]
            : theme.colors[theme.primaryColor][5]
        }
      />
      <Title order={2} fw={500} mt={rem(6)}>
        {value}
      </Title>
    </Box>
  );
};
